/* eslint-disable @typescript-eslint/no-explicit-any */
import apiFetch from "../../../api/apiFetch";
import { getAccessToken } from "../../../context/auth/tokenStore";
import { API_BASE_URL } from "../../../constants/env";

const qs = (params?: Record<string, any>) =>
  params ? "?" + new URLSearchParams(params as any).toString() : "";

const xhrUpload = (
  method: string,
  path: string,
  data: FormData,
  onProgress?: (pct: number) => void,
): Promise<any> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const token = getAccessToken();
    const base = API_BASE_URL.replace(/\/$/, "");
    xhr.open(method, `${base}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable)
        onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    // Fires when server closes the connection mid-upload (e.g. nginx 413 before body is fully sent)
    xhr.upload.onabort = () =>
      reject(
        new Error(
          "File too large — the server rejected the upload. Please use a file under 50MB.",
        ),
      );
    xhr.upload.onerror = () =>
      reject(
        new Error(
          "Network error during upload — check your connection and try again.",
        ),
      );

    xhr.onload = () => {
      if (xhr.status === 413) {
        reject(
          new Error(
            "File too large — the server rejected the upload. Please use a file under 50MB.",
          ),
        );
        return;
      }
      try {
        const response = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) resolve(response);
        else
          reject(
            new Error(response.message || `Upload failed (${xhr.status})`),
          );
      } catch {
        reject(new Error(`Server error (${xhr.status}) — please try again.`));
      }
    };
    xhr.onerror = () =>
      reject(new Error("Network error — check your connection and try again."));
    xhr.send(data);
  });

export const adminAPI = {
  // ── Dashboard ──────────────────────────────────────────
  getStats: () => apiFetch("/admin/stats"),

  // ── Users ──────────────────────────────────────────────
  getUsers: (params?: Record<string, any>) => apiFetch(`/users${qs(params)}`),
  getUserStats: () => apiFetch(`/users/stats`),
  getUser: (id: string | number) => apiFetch(`/users/${id}`),
  createUser: (data: any) =>
    apiFetch("/users", { method: "POST", body: JSON.stringify(data) }),
  updateUser: (id: string | number, data: any) =>
    apiFetch(`/users/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteUser: (id: string | number) =>
    apiFetch(`/users/${id}`, { method: "DELETE" }),
  uploadProfileImage: (
    id: string | number,
    file: File,
  ): Promise<{ profile_image_url: string }> => {
    const fd = new FormData();
    fd.append("image", file);
    return apiFetch(`/users/${id}/profile-image`, {
      method: "POST",
      body: fd,
      headers: {},
    });
  },
  uploadGalleryImages: (
    userId: string | number,
    files: File[],
  ): Promise<any> => {
    const fd = new FormData();
    files.forEach((f) => fd.append("images[]", f));
    return apiFetch(`/users/upload-media/${userId}`, {
      method: "POST",
      body: fd,
      headers: {},
    });
  },
  deleteGalleryImage: (mediaId: number): Promise<any> =>
    apiFetch(`/media/${mediaId}`, { method: "DELETE" }),
  replaceGalleryImage: async (
    mediaId: number,
    userId: string | number,
    file: File,
  ): Promise<{ newItem: { id: number; image_url: string } }> => {
    await apiFetch(`/media/${mediaId}`, { method: "DELETE" });
    const fd = new FormData();
    fd.append("images[]", file);
    const res = await apiFetch(`/users/upload-media/${userId}`, {
      method: "POST",
      body: fd,
      headers: {},
    });
    const uploaded = (res?.media ?? res?.data ?? [])[0];
    return { newItem: uploaded };
  },

  // ── Talents ────────────────────────────────────────────
  getTalents: (params?: Record<string, any>) =>
    apiFetch(`/admin/talents${qs(params)}`),
  getTalentsStats: () => apiFetch(`/admin/talents/stats`),
  approveTalent: (id: string) =>
    apiFetch(`/talents/${id}/approve`, { method: "PUT" }),
  rejectTalent: (id: string, reason: string) =>
    apiFetch(`/talents/${id}/reject`, {
      method: "PUT",
      body: JSON.stringify({ rejection_reason: reason }),
    }),
  createTalent: (data: any) =>
    apiFetch("/talents", { method: "POST", body: JSON.stringify(data) }),

  // ── Blogs ──────────────────────────────────────────────
  getBlogs: (params?: Record<string, any>) =>
    apiFetch(`/admin/blogs/allBlogs${qs(params)}`),
  getBlogStats: (params?: Record<string, any>) =>
    apiFetch(`/admin/blogs/stats${qs(params)}`),
  getBlog: (id: string) => apiFetch(`/blogs/${id}`),
  createBlog: (data: FormData | Record<string, unknown>) =>
    apiFetch("/blogs", {
      method: "POST",
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  updateBlog: (id: string, data: FormData | Record<string, unknown>) => {
    if (data instanceof FormData) {
      data.append("_method", "PUT");
      return apiFetch(`/blogs/${id}`, { method: "POST", body: data });
    }
    return apiFetch(`/blogs/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  deleteBlog: (id: string) => apiFetch(`/blogs/${id}`, { method: "DELETE" }),
  approveBlog: (id: string) =>
    apiFetch(`/admin/blogs/${id}/approve`, {
      method: "PUT",
    }),
  rejectBlog: (id: string, reason: string) =>
    apiFetch(`/admin/blogs/${id}/reject`, {
      method: "PUT",
      body: JSON.stringify({ status: "rejected", rejection_reason: reason }),
    }),

  // ── Categories ─────────────────────────────────────────
  getCategories: () => apiFetch("/categories", { auth: false }),
  createCategory: (data: any) =>
    apiFetch("/categories", {
      method: "POST",
      body: JSON.stringify(data),
      auth: true,
    }),
  updateCategory: (id: string, data: any) =>
    apiFetch(`/categories/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      auth: true,
    }),
  deleteCategory: (id: string) =>
    apiFetch(`/categories/${id}`, { method: "DELETE", auth: true }),

  // ── Specialties ────────────────────────────────────────
  getSpecialties: () => apiFetch("/specialties", { auth: false }),
  createSpecialty: (data: any) =>
    apiFetch("/specialties", {
      method: "POST",
      body: JSON.stringify(data),
      auth: true,
    }),
  updateSpecialty: (id: string, data: any) =>
    apiFetch(`/specialties/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      auth: true,
    }),
  deleteSpecialty: (id: string) =>
    apiFetch(`/specialties/${id}`, { method: "DELETE", auth: true }),

  // ── Contents ───────────────────────────────────────────
  getContents: (params?: Record<string, any>) =>
    apiFetch(`/contents${qs(params)}`),
  createContent: (
    formData: FormData,
    onProgress?: (pct: number) => void,
  ): Promise<any> => xhrUpload("POST", "/contents", formData, onProgress),
  updateContent: (id: string, data: any, onProgress?: (pct: number) => void) => {
    if (data instanceof FormData) {
      data.append("_method", "PUT");
      return xhrUpload("POST", `/contents/${id}`, data, onProgress);
    }
    return apiFetch(`/contents/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  deleteContent: (id: string) =>
    apiFetch(`/contents/${id}`, { method: "DELETE" }),

  // ── Works ──────────────────────────────────────────────
  getWorks: () => apiFetch("/works"),
  getBrands: () => apiFetch("/trusted-brands"),

  createWork: (
    data: FormData,
    onProgress?: (pct: number) => void,
  ): Promise<any> => xhrUpload("POST", "/works", data, onProgress),

  updateWork: (
    id: string,
    data: FormData,
    onProgress?: (pct: number) => void,
  ): Promise<any> => {
    data.append("_method", "PUT");
    return xhrUpload("POST", `/works/${id}`, data, onProgress);
  },
  deleteWork: (id: string) => apiFetch(`/works/${id}`, { method: "DELETE" }),

  // ── Albums ─────────────────────────────────────────────
  getAlbums: () => apiFetch("/albums"),

  createAlbum: (
    data: FormData,
    onProgress?: (pct: number) => void,
  ): Promise<any> => xhrUpload("POST", "/albums", data, onProgress),

  updateAlbum: (
    id: string,
    data: FormData,
    onProgress?: (pct: number) => void,
  ): Promise<any> => {
    data.append("_method", "PUT");
    return xhrUpload("POST", `/albums/${id}`, data, onProgress);
  },
  deleteAlbum: (id: string) => apiFetch(`/albums/${id}`, { method: "DELETE" }),

  // ── Briefs ─────────────────────────────────────────────
  getBriefs: (params?: Record<string, any>) => apiFetch(`/briefs${qs(params)}`),
  updateBriefStatus: (id: string, status: string, notes?: string) =>
    apiFetch(`/admin/briefs/${id}/status`, {
      method: "POST",
      body: JSON.stringify({ status, notes }),
    }),

  // ── Quotations ─────────────────────────────────────────
  getQuotations: (params?: Record<string, any>) =>
    apiFetch(`/quotations${qs(params)}`),
  updateQuotation: (
    id: number,
    data: Partial<{
      name: string;
      email: string;
      phone: string;
      idea: string;
      equipment: string | null;
      status: string;
    }>,
  ) =>
    apiFetch(`/quotations/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  deleteQuotation: (id: number) =>
    apiFetch(`/quotations/${id}`, { method: "DELETE" }),

  // ── Contact Messages ───────────────────────────────────
  getContactMessages: () => apiFetch("/contact-messages"),
  updateContactMessage: (id: number, data: { status: "new" | "read" }) =>
    apiFetch(`/contact-messages/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  deleteContactMessage: (id: number) =>
    apiFetch(`/contact-messages/${id}`, { method: "DELETE" }),

  // ── Trusted Brands ─────────────────────────────────────
  getTrustedBrands: () => apiFetch("/trusted-brands", { auth: false }),
  createTrustedBrand: (data: { name: string; logo: string }) =>
    apiFetch("/admin/trusted-by", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateTrustedBrand: (id: number, data: { name?: string; logo?: string }) =>
    apiFetch(`/admin/trusted-by/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  deleteTrustedBrand: (id: number) =>
    apiFetch(`/admin/trusted-by/${id}`, { method: "DELETE" }),

  // ── Project Types ──────────────────────────────────────
  getProjectTypes: () => apiFetch("/project-types", { auth: false }),
  createProjectType: (data: any) =>
    apiFetch("/project-types", {
      method: "POST",
      body: JSON.stringify(data),
      auth: true,
    }),
  updateProjectType: (id: string | number, data: any) =>
    apiFetch(`/project-types/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      auth: true,
    }),
  deleteProjectType: (id: string | number) =>
    apiFetch(`/project-types/${id}`, { method: "DELETE", auth: true }),

  // ── Testimonials ───────────────────────────────────────
  getTestimonials: () => apiFetch("/testimonials", { auth: false }),
  createTestimonial: (data: any) =>
    apiFetch("/testimonials", { method: "POST", body: JSON.stringify(data) }),
  updateTestimonial: (id: number, data: any) =>
    apiFetch(`/testimonials/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  deleteTestimonial: (id: number) =>
    apiFetch(`/testimonials/${id}`, { method: "DELETE" }),

  // ── About Section ─────────────────────────────────────
  getAboutSection: () => apiFetch("/about-section", { auth: false }),
  updateAboutSection: (data: FormData) =>
    apiFetch("/admin/about-section", {
      method: "POST",
      body: data,
      headers: {},
    }),

  // ── Footer Settings ────────────────────────────────────
  getFooterSettings: () => apiFetch("/footer-settings", { auth: false }),
  updateFooterSettings: (data: Record<string, any>) =>
    apiFetch("/admin/footer-settings", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // ── Questions ──────────────────────────────────────────
  getQuestions: (params?: Record<string, any>) =>
    apiFetch(`/questions/active${qs(params)}`),

  getQuestionsForAdmin: (params?: Record<string, any>) =>
    apiFetch(`/admin/questions${qs(params)}`),

  createQuestion: (data: { text: string; status: "active" | "inactive" }) =>
    apiFetch("/questions", {
      method: "POST",
      body: JSON.stringify({ question: data.text, status: data.status }),
    }),
  updateQuestion: (
    id: number,
    data: { text: string; status: "active" | "inactive" },
  ) =>
    apiFetch(`/questions/${id}`, {
      method: "PUT",
      body: JSON.stringify({ question: data.text, status: data.status }),
    }),
  toggleQuestion: (id: number) =>
    apiFetch(`/questions/${id}/toggle`, { method: "POST" }),
  deleteQuestion: (id: number) =>
    apiFetch(`/questions/${id}`, { method: "DELETE" }),
};
